// Locale-aware week math. en starts on Sunday; CN/TW start on Monday.
// All dates in this module are interpreted in UTC — the cookOn column
// is a DATE, so timezone is meaningless past the day boundary.

type WeekStartDow = 0 | 1; // 0 = Sunday, 1 = Monday

export function weekStartDowForLocale(locale: string): WeekStartDow {
  if (locale.startsWith("zh")) return 1;
  return 0;
}

// Snap an arbitrary date to the start of its locale-aware week. Returns
// a UTC midnight Date.
export function startOfWeek(date: Date, dow: WeekStartDow): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const diff = (d.getUTCDay() - dow + 7) % 7;
  d.setUTCDate(d.getUTCDate() - diff);
  return d;
}

// Add a number of days, returning a fresh UTC Date.
export function addDays(date: Date, days: number): Date {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

// "2026-05-08" → Date at UTC midnight. Returns null on bad input, and on a date that does not exist: Date.UTC rolls
// "2026-02-31" over to March 3 and reads year 0050 as 1950, so only a date that reads back as typed is real.
export function parseIsoDate(input: string | undefined | null): Date | null {
  if (!input) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return null;
  if (toIsoDate(d) !== input) return null;
  return d;
}

export function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// Return the user's local date as YYYY-MM-DD. Unlike toIsoDate(new Date()),
// this uses local timezone methods so 11 PM PDT on May 18 returns "2026-05-18"
// instead of the UTC date "2026-05-19".
export function localToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Format a 7-day range header, e.g. "Apr 27 – May 3, 2026". Uses
// Intl.DateTimeFormat for locale-correct month abbreviations.
export function formatWeekRangeLabel(start: Date, locale: string): string {
  const end = addDays(start, 6);
  const fmt = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const yearFmt = new Intl.DateTimeFormat(locale, { year: "numeric", timeZone: "UTC" });
  const sameMonth =
    start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear();
  const startStr = fmt.format(start);
  const endStr = sameMonth
    ? new Intl.DateTimeFormat(locale, { day: "numeric", timeZone: "UTC" }).format(end)
    : fmt.format(end);
  return `${startStr} – ${endStr}, ${yearFmt.format(start)}`;
}

// Eyebrow label for the calendar header — "May · Week 1" style.
// Anchored to the END of the visible week so weeks that straddle two
// months belong to the month they end in (matches the mockup case where
// Apr 26–May 2 reads as "May · Week 1").
export function formatWeekEyebrow(start: Date, locale: string): string {
  const end = addDays(start, 6);
  const monthStr = new Intl.DateTimeFormat(locale, {
    month: "short",
    timeZone: "UTC",
  }).format(end);
  const weekOfMonth = Math.ceil(end.getUTCDate() / 7);
  return `${monthStr} · Week ${weekOfMonth}`;
}

// True when `today` falls inside the 7-day window starting at `start`.
export function isCurrentWeek(start: Date, today: Date): boolean {
  const startMs = start.getTime();
  const endMs = addDays(start, 7).getTime();
  const todayMs = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return todayMs >= startMs && todayMs < endMs;
}
